import { API } from './index'
import type {
  ChangeSeenDto,
  SibValueChangeListResponse,
  ValueChangeType,
} from './dto/unionChanges.dto'

/**
 * Rows per backend request. The change list loads every page of the chosen period, like the
 * dashboard loads every SIB, and searches, sorts and pages in the browser.
 */
export const UNION_CHANGES_PAGE_SIZE = 100

/**
 * What the backend contract (SIB-936) provides for this feature, verified against
 * `sib-backend` origin/develop on 2026-09-23.
 *
 * One thing the frontend ticket (SIB-937) asks for has no backend counterpart yet:
 *
 * - `undoSeen`: there is no endpoint that removes a seen mark (`UnionChangeSeen` is insert-only).
 *   The undo action stays hidden until the backend provides the provisional
 *   `DELETE /union/changes/{changeType}/{changeId}/seen` used by {@link undoUnionChangeSeen}.
 *
 * Flip the flag once the endpoint exists; the UI, hook and tests already cover it.
 */
export interface UnionChangesCapabilities {
  undoSeen: boolean
}

export const UNION_CHANGES_CONTRACT: UnionChangesCapabilities = {
  undoSeen: false,
}

export interface UnionChangeListParams {
  /** First day of the period (by the day the change was saved), `yyyy-MM-dd`, inclusive. */
  from?: string | null
  /** Last day of the period, `yyyy-MM-dd`, inclusive. */
  to?: string | null
  sibUserId?: string | null
  /** 1-based. */
  page?: number
  pageSize?: number
}

// The list reflects marks colleagues set at any moment, so it is never served from the 30 s GET
// cache in `API`: every request goes to the backend.
const FRESH = { headers: { 'X-Disable-Cache': '1' } }

/**
 * One page of price and capacity changes across all SIBs, newest first. Prices are scoped to
 * the Union bank by the backend; a manager never receives prices agreed with another bank.
 */
export function listUnionChanges(
  params: UnionChangeListParams = {}
): Promise<SibValueChangeListResponse> {
  const query = new URLSearchParams()
  if (params.from) query.set('from', params.from)
  if (params.to) query.set('to', params.to)
  if (params.sibUserId) query.set('sibUserId', params.sibUserId)
  query.set('page', String(params.page ?? 1))
  query.set('pageSize', String(params.pageSize ?? UNION_CHANGES_PAGE_SIZE))

  return API<SibValueChangeListResponse>(
    `/union/changes?${query.toString()}`,
    FRESH,
    'Die Änderungen konnten nicht geladen werden.',
    'UNION_CHANGES_LOAD_FAILED'
  )
}

/**
 * Marks one change as seen for the whole coordination group. Idempotent: if a colleague was
 * first, their mark comes back unchanged.
 */
export function markUnionChangeSeen(
  changeType: ValueChangeType,
  changeId: number
): Promise<ChangeSeenDto> {
  return API<ChangeSeenDto>(
    `/union/changes/${changeType}/${changeId}/seen`,
    { method: 'POST' },
    'Die Änderung konnte nicht als gesehen markiert werden.',
    'UNION_CHANGE_SEEN_FAILED'
  )
}

/**
 * Removes the team-wide seen mark from one change.
 *
 * Provisional contract, see {@link UNION_CHANGES_CONTRACT}: the backend does not provide this
 * endpoint yet, and the UI does not offer the action until `undoSeen` is switched on.
 */
export async function undoUnionChangeSeen(
  changeType: ValueChangeType,
  changeId: number
): Promise<void> {
  await API<void>(
    `/union/changes/${changeType}/${changeId}/seen`,
    { method: 'DELETE' },
    'Die Gesehen-Markierung konnte nicht entfernt werden.',
    'UNION_CHANGE_UNDO_SEEN_FAILED'
  )
}
